import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, Pressable, StyleSheet } from 'react-native';
import CrashTester from 'react-native-crash-tester';

import { datadog } from '@sg/expo-datadog-client';
import { BodyText, theme, usePressableState, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

/**
 * This component will, if the feature flag is on, render two
 * options in the account menu that allows crashing the app
 * with either a JS error or a native error.
 *
 * To be updated by https://sweetgreen.atlassian.net/browse/XP-3223
 */

export const AccountMenuCrashOptions = () => {
  const { formatMessage } = useIntl();
  const isEnabled = useFeatureFlag('permanent-order-crash-option-enabled');
  const { APP_VERSION_DATADOG: datadogVersion } = getEnvVars();

  const handleJsCrash = useCallback(() => {
    void datadog.reportError(new Error(`Expected JS Crash #${datadogVersion}`), { expected: true });
    CrashTester.jsCrash(`Expected JS Crash #${datadogVersion}`);
  }, [datadogVersion]);

  const handleNativeCrash = useCallback(() => {
    void datadog.reportError(new Error(`Expected Native Crash #${datadogVersion}`), {
      expected: true,
    });
    CrashTester.nativeCrash(`Expected Native Crash #${datadogVersion}`);
  }, [datadogVersion]);

  if (!isEnabled) return null;

  return (
    <>
      <CrashOption label={formatMessage(messages.accountSidebarJsCrash)} onPress={handleJsCrash} />
      {Platform.OS === 'web' ? null : (
        <CrashOption
          label={formatMessage(messages.accountSidebarNativeCrash)}
          onPress={handleNativeCrash}
        />
      )}
    </>
  );
};

const CrashOption = ({ label, onPress }: CrashOptionProps) => {
  const ref = React.useRef(null);
  const { match } = useResponsive();
  const { isInteracting } = usePressableState(ref);

  return (
    <Pressable ref={ref} style={styles.container} accessibilityLabel={label} onPress={onPress}>
      <BodyText size={match([2, 3])} underline={isInteracting}>
        {label}
      </BodyText>
    </Pressable>
  );
};

type CrashOptionProps = Readonly<{
  label: string;
  onPress: () => void;
}>;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: theme.radius.small,
    paddingVertical: theme.spacing['2'],
    marginVertical: theme.spacing['2'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountSidebarJsCrash: {
    defaultMessage: 'Javascript Crash',
    description: 'Account | Sidebar | Js Crash',
  },
  accountSidebarNativeCrash: {
    defaultMessage: 'Native Crash',
    description: 'Account | Sidebar | Native Crash',
  },
});
